import {
  estPinTrivial,
  normalisePhone,
  PHONE_MIN_DIGITS,
  PIN_TRIVIAL_MESSAGE,
  PREFIXE_MALI,
  VILLES_INSCRIPTION,
} from "@hakili/domain-types";
import { t } from "@hakili/i18n";

import { masque } from "./connexion.js";
import {
  BOUTON_TEXTE,
  Button,
  Card,
  classes,
  RAYON_CONTROLE,
  SURFACE_BORDER,
  TEXTE_PRINCIPAL,
} from "@hakili/ui";
import { useState } from "react";

import {
  DeclaredProfileFields,
  emptyDraft,
  toWrite,
  validateDraft,
  type ProfileWrite,
} from "./declared-profile.js";

/**
 * `P1-CLW-007`: WF-CLI-01, `Création de compte`, as its five screens.
 *
 * **Every guard here is the workflow's, and every one of them also runs on the
 * server.** WF-CLI-01 states them as UI rules , the phone needs eight digits
 * *"otherwise `Recevoir mon code` is disabled (no message, the control is simply
 * inactive)"*, the name needs more than two characters, the PIN must not be
 * trivial and shows an exact sentence when it is. A disabled control is a
 * courtesy; the refusal is the API's, and `@hakili/domain-types` is where both
 * sides read the rule so they cannot disagree.
 *
 * **`DECISION ADDED` on this workflow, applied**: *"the mockup labels the OTP as
 * SMS and offers WhatsApp as a fallback. The spine mandates the opposite.
 * Implement WhatsApp first with SMS fallback."* So the code screen says WhatsApp
 * and offers SMS as the secondary action, and the channel it names is the one
 * the SERVER reported carrying it , not the one this app hoped for.
 */

const CHAMP = "w-full rounded-xl border border-gray-200 px-3 py-3 text-base";
/** The country-code field, in the design system's vocabulary rather than in ad-hoc classes. */
const INDICATIF = "w-20 shrink-0 border px-3 py-3 text-base";

/** WF-CLI-01, step 1. Three value propositions and two ways in. */
export function Bienvenue({
  creer,
  connexion,
}: {
  readonly creer: () => void;
  readonly connexion: () => void;
}): React.JSX.Element {
  return (
    <Card shape="client" className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-gray-900">{t("client.bienvenue.titre")}</h1>
      <ul className="flex flex-col gap-2 text-sm text-gray-500">
        <li>{t("client.bienvenue.argument1")}</li>
        <li>{t("client.bienvenue.argument2")}</li>
        <li>{t("client.bienvenue.argument3")}</li>
      </ul>
      <Button onClick={creer}>{t("client.bienvenue.creer")}</Button>
      <button type="button" className={`text-sm ${BOUTON_TEXTE}`} onClick={connexion}>
        {t("client.bienvenue.jaiUnCompte")}
      </button>
    </Card>
  );
}

/** WF-CLI-01, step 2. The prefix is fixed and the field is the rest. */
export function Numero({
  demander,
  refusal,
}: {
  readonly demander: (phone: string) => Promise<void>;
  readonly refusal?: string | undefined;
}): React.JSX.Element {
  const [chiffres, setChiffres] = useState("");
  // **PRESELECTED, never fixed , the founder's decision D1 of 3 September 2026.**
  // Hakili moves to other West African countries regardless; a client's WhatsApp
  // may live on a different number than his SIM; and testing runs on a Canadian
  // number. The mockup draws this prefix as fixed text and is SUPERSEDED on this
  // point (D2); the copy *« le numéro est votre identifiant »* stands.
  const [indicatif, setIndicatif] = useState(PREFIXE_MALI);
  const [enCours, setEnCours] = useState(false);
  // **The wire carries E.164**, whatever the two fields showed.
  const numero = `${normalisePhone(indicatif)}${normalisePhone(chiffres)}`;
  // "no message, the control is simply inactive" , WF-CLI-01's own words. A
  // person typing their number has not made a mistake yet.
  //
  // **This length rule is Mali's, and it is knowingly provisional.** Whether a
  // number is valid FOR ITS COUNTRY is per-country parsing, which is
  // `P0-IDN-051`'s domain half; until it lands the control uses the one rule
  // both sides already share, so the screen still never refuses a number the
  // door would accept.
  const pret =
    normalisePhone(chiffres).replaceAll(/\D/gu, "").length >= PHONE_MIN_DIGITS &&
    /^\+\d{1,4}$/u.test(normalisePhone(indicatif));

  const soumettre = (): void => {
    if (!pret || enCours) return;
    setEnCours(true);
    void demander(numero).finally(() => {
      setEnCours(false);
    });
  };

  return (
    <Card shape="client" className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-gray-900">{t("client.numero.titre")}</h1>
      {/**
       * **A real form, because the buyer's gesture is the point.** Chrome named
       * the missing one at the sitting of 29 August 2026 , `[DOM] Password field
       * is not contained in a form` , and the cost is not the warning: a handset
       * offers no `Valider` on its keyboard and `Enter` does nothing, so a person
       * must reach for the button on every screen. `noValidate` because the guard
       * is `inactive` and not the browser's bubble, and every disabled rule is
       * unchanged.
       */}
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          soumettre();
        }}
      >
        <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="inscription-numero">
          {t("client.numero.champ")}
          <span className="flex items-center gap-2">
            {/* The country code is a CONTROL now, not a caption. `w-20` because
                the longest code is four digits and a field wide enough for a
                number invites one to be typed into it. */}
            <input
              id="inscription-indicatif"
              className={classes(INDICATIF, RAYON_CONTROLE, SURFACE_BORDER, TEXTE_PRINCIPAL)}
              inputMode="tel"
              autoComplete="tel-country-code"
              aria-label={t("client.numero.indicatif")}
              value={indicatif}
              onChange={(event) => {
                setIndicatif(event.target.value);
              }}
            />
            <input
              id="inscription-numero"
              className={CHAMP}
              inputMode="numeric"
              autoComplete="tel-national"
              value={chiffres}
              onChange={(event) => {
                setChiffres(event.target.value);
              }}
            />
          </span>
        </label>
        <p className="text-sm text-gray-500">{t("client.numero.identifiant")}</p>
        <p className="text-sm text-gray-500">{t("client.numero.consentement")}</p>
        <p className="text-sm text-gray-500">{t("client.numero.ceQueNousNeDemandonsPas")}</p>
        {refusal === undefined ? null : (
          <p role="alert" className="text-sm text-rose-700">
            {refusal}
          </p>
        )}
        <Button type="submit" inactive={!pret || enCours}>
          {t("client.numero.recevoir")}
        </Button>
      </form>
    </Card>
  );
}

/** WF-CLI-01, step 3. Six digits, and the channel the SERVER reported. */
export function CodeVerif({
  phone,
  canal,
  confirmer,
  renvoyer,
  refusal,
}: {
  readonly phone: string;
  readonly canal: string;
  readonly confirmer: (code: string) => Promise<void>;
  readonly renvoyer: () => Promise<void>;
  readonly refusal?: string | undefined;
}): React.JSX.Element {
  const [code, setCode] = useState("");
  const [enCours, setEnCours] = useState(false);

  const soumettre = (): void => {
    if (code.length !== 6 || enCours) return;
    setEnCours(true);
    void confirmer(code).finally(() => {
      setEnCours(false);
    });
  };

  return (
    <Card shape="client" className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-gray-900">{t("client.code.titre")}</h1>
      {/* The channel is REPORTED, not assumed: `otp` answers which one carried
          it, and a screen that always said WhatsApp would be wrong exactly when
          the fallback mattered. */}
      {/* **Masked, and the country code stays** , D2. The record's own copy is
          `Envoyé sur WhatsApp au +223 xx xx xx xx`, and this screen printed the
          number in full: a handset on a table showed whose account it was to
          anybody who picked it up, on the one screen a person stares at while
          waiting for a code. */}
      <p className="text-sm text-gray-500">{`${canal === "sms" ? "SMS" : "WhatsApp"} : ${masque(phone)}`}</p>
      {/**
       * **A real form, because the buyer's gesture is the point.** Chrome named
       * the missing one at the sitting of 29 August 2026 , `[DOM] Password field
       * is not contained in a form` , and the cost is not the warning: a handset
       * offers no `Valider` on its keyboard and `Enter` does nothing, so a person
       * must reach for the button on every screen. `noValidate` because the guard
       * is `inactive` and not the browser's bubble, and every disabled rule is
       * unchanged.
       */}
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          soumettre();
        }}
      >
        <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="inscription-code">
          {t("client.code.champ")}
          <input
            id="inscription-code"
            className={CHAMP}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replaceAll(/\D/gu, ""));
            }}
          />
        </label>
        {refusal === undefined ? null : (
          <p role="alert" className="text-sm text-rose-700">
            {refusal}
          </p>
        )}
        <Button type="submit" inactive={code.length !== 6 || enCours}>
          {t("client.code.confirmer")}
        </Button>
      </form>
      <button
        type="button"
        className={`text-sm ${BOUTON_TEXTE}`}
        onClick={() => {
          void renvoyer();
        }}
      >
        {t("client.code.renvoyer")}
      </button>
    </Card>
  );
}

/** WF-CLI-01, step 4. The name and one of four cities. */
export function ProfilInscription({
  enregistrer,
  refusal,
}: {
  readonly enregistrer: (fullName: string, city: string, profile: ProfileWrite) => Promise<void>;
  readonly refusal?: string | undefined;
}): React.JSX.Element {
  const [nom, setNom] = useState("");
  const [draft, setDraft] = useState(emptyDraft);
  const [tente, setTente] = useState(false);
  const [ville, setVille] = useState(VILLES_INSCRIPTION[0] ?? "");
  const [enCours, setEnCours] = useState(false);
  const pret = nom.trim().length > 2 && ville !== "";

  return (
    <Card shape="client" className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-gray-900">{t("client.profil.titre")}</h1>
      <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="inscription-nom">
        {t("client.profil.nom")}
        <input
          id="inscription-nom"
          className={CHAMP}
          autoComplete="name"
          value={nom}
          onChange={(event) => {
            setNom(event.target.value);
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="inscription-ville">
        {t("client.profil.ville")}
        {/* A closed list and not a text field: the API refuses a city outside
            it, and a free field would put "bamako", "BKO" and "Bamako " in one
            column with every count over it wrong in a way nobody could see. */}
        <select
          id="inscription-ville"
          className={CHAMP}
          value={ville}
          onChange={(event) => {
            setVille(event.target.value);
          }}
        >
          {VILLES_INSCRIPTION.map((une) => (
            <option key={une} value={une}>
              {une}
            </option>
          ))}
        </select>
      </label>
      <DeclaredProfileFields draft={draft} onChange={setDraft} attempted={tente} />
      <p className="text-sm text-gray-500">{t("client.profil.plusTard")}</p>
      {refusal === undefined ? null : (
        <p role="alert" className="text-sm text-rose-700">
          {refusal}
        </p>
      )}
      <Button
        data-testid="inscription-continuer"
        inactive={!pret || enCours}
        onClick={() => {
          setTente(true);
          if (Object.keys(validateDraft(draft)).length > 0) return;
          setEnCours(true);
          void enregistrer(nom.trim(), ville, toWrite(draft)).finally(() => {
            setEnCours(false);
          });
        }}
      >
        {t("client.profil.continuer")}
      </Button>
    </Card>
  );
}

/** WF-CLI-01, step 5. The device code, refused before it is sent when it is trivial. */
export function ChoixCode({
  choisir,
  refusal,
}: {
  readonly choisir: (pin: string) => Promise<void>;
  readonly refusal?: string | undefined;
}): React.JSX.Element {
  const [pin, setPin] = useState("");
  const [enCours, setEnCours] = useState(false);
  const complet = pin.length === 4;
  const trivial = complet && estPinTrivial(pin);

  const soumettre = (): void => {
    if (!complet || trivial || enCours) return;
    setEnCours(true);
    void choisir(pin).finally(() => {
      setEnCours(false);
    });
  };

  return (
    <Card shape="client" className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-gray-900">{t("client.choixCode.titre")}</h1>
      {/**
       * **A real form, because the buyer's gesture is the point.** Chrome named
       * the missing one at the sitting of 29 August 2026 , `[DOM] Password field
       * is not contained in a form` , and the cost is not the warning: a handset
       * offers no `Valider` on its keyboard and `Enter` does nothing, so a person
       * must reach for the button on every screen. `noValidate` because the guard
       * is `inactive` and not the browser's bubble, and every disabled rule is
       * unchanged.
       */}
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          soumettre();
        }}
      >
        <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="inscription-pin">
          {t("client.choixCode.champ")}
          <input
            id="inscription-pin"
            className={CHAMP}
            inputMode="numeric"
            type="password"
            // A code being CHOSEN, not one being recalled , the handset should
            // offer to remember it rather than to fill it.
            autoComplete="new-password"
            maxLength={4}
            value={pin}
            onChange={(event) => {
              setPin(event.target.value.replaceAll(/\D/gu, ""));
            }}
          />
        </label>
        <p className="text-sm text-gray-500">{t("client.choixCode.surCetAppareil")}</p>
        {/* The exact sentence WF-CLI-01 shows, read from the shared vocabulary so
          this screen and the API's refusal are the same string. */}
        {trivial ? (
          <p role="alert" className="text-sm text-rose-700">
            {PIN_TRIVIAL_MESSAGE}
          </p>
        ) : null}
        {refusal === undefined ? null : (
          <p role="alert" className="text-sm text-rose-700">
            {refusal}
          </p>
        )}
        <Button type="submit" inactive={!complet || trivial || enCours}>
          {t("client.choixCode.creer")}
        </Button>
      </form>
    </Card>
  );
}
